// src/components/layouts/header/settings/tabs/AddNewCompany.jsx
//import { useRef, useState } from 'react'
//import iconLapiz from '../../../../../assets/iconos/iconLapiz.png'
import * as Yup from 'yup'
import tituloLoginSII from '../../../../../assets/tituloLoginSII.png'
import { Formik, Form } from 'formik'
import { FieldWithErrorMessage } from '../../../../modules/ui/FieldWithErrorMsg'
import { sileo } from 'sileo'
import { formatRutForBackend } from '../../../../utils/formatter'
import { useGlobalContext } from '../../../../../context/GlobalState'
import { useMutationCreateBusiness } from '../../../../../queries/businessQueries'
import { encryptWithSecret } from '../../../../../utils/encrypt.util'
import organizationApi from '../../../../../api/organization.api'
import { BusinessAPI } from '../../../../../api/business.api'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '../../../../../queries/querykeys'
//import { BaseHTTP } from '../../../../../api/base.http'

// Worker integration
/*const createSummaryWorker = () => new Worker(
  new URL('./SummaryWorker.js', import.meta.url),
  { type: 'module' }
)*/

/**
 * @param {{ onClose: () => void }} props
 */
export const AddNewCompany = ({ onClose }) => {
  const COMPANY_SYNC_TOAST_ID = 'add-company-sync-toast'
  const COMPANY_SYNC_STATUS_TOAST_ID = 'add-company-sync-status-toast'
  const createBusiness = useMutationCreateBusiness()
  const queryClient = useQueryClient()
  const businessAPI = new BusinessAPI()
  const {
    business,
    setBusiness,
    ui: { setLoader }
  } = useGlobalContext()
  //const [, setImageUrl] = useState('https://placehold.co/400')
  //const imgFileRef = useRef(null)

  // Validation schema
  const schemaValidation = Yup.object().shape({
    user: Yup.string()
      .required('Ingresa tu RUT')
      .test(
        'unique-user',
        'Este RUT ya está en uso',
        value => !business.some(b => b.rut === formatRutForBackend(value))
      ),
    password: Yup.string().required('Ingresa tu clave tributaria')
    //nombreFantasia: Yup.string().required('Ingresa el nombre de la empresa')
  })

  const normalizeRut = value => String(value || '').replace(/[^0-9kK]/g, '').toUpperCase()
  const isEncryptionUnavailableError = error =>
    error?.message === 'ENCRYPT_SECRET no configurado' || error?.message === 'Web Crypto no disponible'
  const shouldRetryLegacyPayload = error => {
    const status = error?.response?.status
    return status === 500 || status === 502 || status === 503 || status === 504
  }

  const buildCreateBusinessPayload = async values => {
    const normalizedRut = formatRutForBackend(values.user).trim()
    const cleanPassword = String(values.password || '').trim()
    const legacyPayload = {
      user: normalizedRut,
      password: cleanPassword
    }

    const encryptSecret = typeof window !== 'undefined' ? window.__APP_CONFIG__?.encryptSecret : ''
    if (!encryptSecret || !globalThis.crypto?.subtle) {
      return { normalizedRut, legacyPayload, encryptedPayload: null }
    }

    try {
      const [encryptedUser, encryptedPassword] = await Promise.all([
        encryptWithSecret(normalizedRut, encryptSecret),
        encryptWithSecret(cleanPassword, encryptSecret)
      ])

      const encryptedPayload = {
        user: encryptedUser,
        password: encryptedPassword
      }

      return { normalizedRut, encryptedPayload, legacyPayload }
    } catch (error) {
      if (isEncryptionUnavailableError(error)) {
        return { normalizedRut, legacyPayload, encryptedPayload: null }
      }
      throw error
    }
  }

  const hasVisionDelNegocioReady = latestVision => {
    if (!latestVision || typeof latestVision !== 'object') return false
    const values = Object.values(latestVision)
    if (values.length === 0) return false
    return values.some(value => {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim().length > 0
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'object') return Object.keys(value).length > 0
      return true
    })
  }

  const hasBusinessInsightsLoaded = async businessId => {
    const latestResponse = await businessAPI.getLatestVisionDelNegocio({ businessId })
    if (latestResponse?.status !== 200) return false
    if (!latestResponse?.data || typeof latestResponse?.data !== 'object') return false
    return hasVisionDelNegocioReady(latestResponse.data)
  }

  const hasRowsInSummaryResponse = payload => {
    if (!payload) return false
    if (Array.isArray(payload)) return payload.length > 0
    if (Array.isArray(payload?.libros)) return payload.libros.length > 0
    if (typeof payload?.count === 'number') return payload.count > 0
    return false
  }

  const hasClientAndProviderSummariesLoaded = async businessId => {
    const currentYear = new Date().getFullYear()
    const baseParams = {
      businessId,
      year: currentYear,
      month: 0,
      linkage: true,
      max: 1,
      page: 0
    }

    const [clientesResponse, proveedoresResponse] = await Promise.all([
      businessAPI.getListSummaryMovementsSpecific({
        ...baseParams,
        type: 'ingreso'
      }),
      businessAPI.getListSummaryMovementsSpecific({
        ...baseParams,
        type: 'egreso'
      })
    ])

    return (
      hasRowsInSummaryResponse(clientesResponse?.data) &&
      hasRowsInSummaryResponse(proveedoresResponse?.data)
    )
  }

  const recoverBusinessIfAlreadyCreated = async normalizedRut => {
    const maxAttempts = 3
    const intervalMs = 5000

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const response = await organizationApi.getMyBusinesses()
        if (response?.status === 200 && Array.isArray(response?.data)) {
          const businessesSynced = response.data
          queryClient.setQueryData([QueryKeys.GET_BUSINESSES_ALL], businessesSynced)
          const syncedBusiness = businessesSynced.find(
            item => normalizeRut(item?.rut) === normalizeRut(normalizedRut)
          )
          if (syncedBusiness) {
            return { businessesSynced, syncedBusiness }
          }
        }
      } catch (error) {
        console.error('Error validando empresa ya creada por RUT:', error)
      }

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, intervalMs))
      }
    }

    return null
  }

  const waitForBusinessReady = async (
    createdBusiness,
    config = { intervalMs: 60_000, maxAttempts: 8 }
  ) => {
    const intervalMs = config?.intervalMs ?? 60_000
    const maxAttempts = config?.maxAttempts ?? 8
    let lastBusinessesSynced = []
    let lastSyncedBusiness = null
    let summariesReady = false

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const response = await organizationApi.getMyBusinesses()
        if (response.status === 200 && Array.isArray(response.data)) {
          const businessesSynced = response.data
          lastBusinessesSynced = businessesSynced
          queryClient.setQueryData([QueryKeys.GET_BUSINESSES_ALL], businessesSynced)

          const syncedBusiness = businessesSynced.find(item => {
            if (createdBusiness?.id) return item.id === createdBusiness.id
            if (createdBusiness?.rut) {
              return normalizeRut(item.rut) === normalizeRut(createdBusiness.rut)
            }
            return false
          })

          if (syncedBusiness) {
            lastSyncedBusiness = syncedBusiness
            try {
              const hasSummaryData = await hasClientAndProviderSummariesLoaded(syncedBusiness.id)
              if (hasSummaryData) {
                summariesReady = true
                const hasLoadedData = await hasBusinessInsightsLoaded(syncedBusiness.id)
                if (hasLoadedData) {
                  return { status: 'ready', businessesSynced, syncedBusiness }
                }
              }
            } catch (detailsError) {
              console.error('Error obteniendo datos de la empresa recién creada:', detailsError)
            }
          }
        }
      } catch (error) {
        console.error('Error sincronizando empresas:', error)
      }

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, intervalMs))
      }
    }

    if (lastSyncedBusiness && summariesReady) {
      return {
        status: 'summaries_ready_latest_pending',
        businessesSynced: lastBusinessesSynced,
        syncedBusiness: lastSyncedBusiness
      }
    }

    if (lastSyncedBusiness) {
      return {
        status: 'listed_only',
        businessesSynced: lastBusinessesSynced,
        syncedBusiness: lastSyncedBusiness
      }
    }

    return { status: 'not_listed' }
  }

  const resolveRecoveredBusinessReadiness = async syncedBusiness => {
    try {
      const hasSummaryData = await hasClientAndProviderSummariesLoaded(syncedBusiness.id)
      if (!hasSummaryData) {
        return { status: 'listed_only' }
      }
      const latestReady = await hasBusinessInsightsLoaded(syncedBusiness.id)
      if (!latestReady) {
        return { status: 'summaries_ready_latest_pending' }
      }
      return { status: 'ready' }
    } catch (error) {
      console.error('Error validando disponibilidad de empresa recuperada:', error)
      return { status: 'listed_only' }
    }
  }

  const startBackgroundBusinessSync = createdBusiness => {
    const backgroundSyncPromise = waitForBusinessReady(createdBusiness, {
      intervalMs: 60_000,
      maxAttempts: 30
    })
    const syncToastPromise = backgroundSyncPromise.then(result => {
      if (result?.status !== 'ready') {
        const pendingSyncError = new Error('BUSINESS_PENDING_SYNC')
        pendingSyncError.syncResult = result
        throw pendingSyncError
      }
      return result
    })

    void sileo.promise(syncToastPromise, {
      loading: {
        id: COMPANY_SYNC_TOAST_ID,
        title: 'Cargando empresa...',
        description: (
          <span className='text-blue-500/80 font-medium'>
            Estamos preparando tu información. Esto puede tardar unos minutos.
          </span>
        )
      },
      success: {
        id: COMPANY_SYNC_TOAST_ID,
        title: 'Empresa lista',
        description: <span className='text-green-500/80 font-medium'>Tu empresa ya está disponible.</span>
      },
      error: {
        id: COMPANY_SYNC_TOAST_ID,
        title: 'La empresa sigue cargando',
        description: 'Seguimos preparando tu información. Puedes continuar usando Luca.'
      }
    })

    void backgroundSyncPromise
      .then(result => {
        if (result?.businessesSynced) {
          setBusiness(result.businessesSynced)
        }

        if (result?.status === 'ready') {
          return
        }

        if (result?.status === 'summaries_ready_latest_pending') {
          sileo.info({
            id: COMPANY_SYNC_STATUS_TOAST_ID,
            title: 'Empresa creada, cargando panel...',
            description: 'Seguimos preparando tu información. Puedes continuar usando Luca.'
          })
        } else {
          sileo.info({
            id: COMPANY_SYNC_STATUS_TOAST_ID,
            title: 'La empresa sigue cargando',
            description: 'Seguimos preparando tu información. Puedes continuar usando Luca.'
          })
        }
      })
      .catch(error => {
        console.error('Error en sincronización de empresa en segundo plano:', error)
        sileo.info({
          id: COMPANY_SYNC_STATUS_TOAST_ID,
          title: 'La empresa sigue cargando',
          description: 'Seguimos preparando tu información. Puedes continuar usando Luca.'
        })
      })
  }

  // Submit handler
  const handleSubmitForm = async (values, actions) => {
    actions.setSubmitting(true)
    setLoader({ visible: true, text: 'Validando datos SII...' })

    // sileo does not have a manual loading handle,
    // we will use sileo.promise or manual success/error calls.
    // Removing toast.loading.

    let createdBusiness = null
    let creationAccepted = false

    try {
      const { normalizedRut, encryptedPayload, legacyPayload } = await buildCreateBusinessPayload(values)
      let createdBusinessResponse

      if (encryptedPayload) {
        try {
          createdBusinessResponse = await createBusiness.mutateAsync(encryptedPayload)
        } catch (error) {
          if (!shouldRetryLegacyPayload(error)) {
            throw error
          }
          createdBusinessResponse = await createBusiness.mutateAsync(legacyPayload)
        }
      } else {
        createdBusinessResponse = await createBusiness.mutateAsync(legacyPayload)
      }
      creationAccepted = true

      if (createdBusinessResponse && typeof createdBusinessResponse === 'object') {
        createdBusiness = {
          ...createdBusinessResponse,
          rut: createdBusinessResponse.rut || normalizedRut
        }
      } else {
        // Algunos flujos responden 200 sin body completo; usamos RUT para sincronizar en selector.
        createdBusiness = { rut: normalizedRut }
      }

      setLoader({ visible: false, text: '' })
      actions.resetForm()
      onClose()
      startBackgroundBusinessSync(createdBusiness)
      return
    } catch (err) {
      console.error(err)

      const normalizedRut = formatRutForBackend(values.user)

      if (!creationAccepted) {
        const recoveredBusiness = await recoverBusinessIfAlreadyCreated(normalizedRut)
        if (recoveredBusiness?.syncedBusiness) {
          setBusiness(recoveredBusiness.businessesSynced)
          const recoveredReadiness = await resolveRecoveredBusinessReadiness(
            recoveredBusiness.syncedBusiness
          )
          const recoveredReady = recoveredReadiness.status === 'ready'
          const recoveredIndicatorText =
            recoveredReadiness.status === 'summaries_ready_latest_pending'
              ? 'Empresa creada, cargando panel...'
              : recoveredReadiness.status === 'listed_only'
                ? 'Empresa creada, cargando documentos...'
                : 'Empresa lista'

          actions.resetForm()
          onClose()

          if (recoveredReady) {
            sileo.success({
              title: 'Empresa lista',
              description: <span className='text-green-500/80 font-medium'>Tu empresa ya está disponible.</span>
            })
          } else if (recoveredIndicatorText === 'Empresa creada, cargando panel...') {
            sileo.info({
              title: 'Empresa creada, cargando panel...',
              description: 'Seguimos preparando tu información. Puedes continuar usando Luca.'
            })
          } else {
            sileo.info({
              title: 'La empresa sigue cargando',
              description: 'Seguimos preparando tu información. Puedes continuar usando Luca.'
            })
          }
          return
        }
      }

      const status = err?.response?.status
      const backendMessage = err?.response?.data?.message
      const backendMessageText = Array.isArray(backendMessage)
        ? backendMessage.join('. ')
        : typeof backendMessage === 'string'
          ? backendMessage
          : ''

      let message = 'No pudimos crear la empresa. Inténtalo de nuevo.'

      if (status === 409) {
        message = 'Llegaste al límite de empresas de tu plan.'
      } else if (status === 401) {
        message = 'RUT o clave tributaria incorrectos.'
      } else if (isEncryptionUnavailableError(err)) {
        message = 'No pudimos procesar tus datos. Inténtalo de nuevo.'
      } else if (backendMessageText) {
        message = backendMessageText
      }

      sileo.error({
        title: 'No se pudo agregar la empresa',
        description: <span className='text-red-500/80 font-medium'>{message}</span>
      })
    } finally {
      setLoader({ visible: false, text: '' })
      actions.setSubmitting(false)
    }
  }

  // Image upload handler
  /*const handleImageUpload = (e) => {
    try {
      const file = e.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onloadend = () => setImageUrl(reader.result)
        reader.readAsDataURL(file)
        imgFileRef.current = file
      }
    } catch (error) {
      console.error('Error leyendo la imagen:', error)
    }
  }*/

  return (
    <section className='py-2 flex flex-col items-center'>
      <h3 className='font-bold text-xl mb-4 font-sans text-center'>Agregar nueva empresa</h3>

      <div className='w-full max-w-md'>
        <Formik
          validationSchema={schemaValidation}
          initialValues={{ user: '', password: '', nombreFantasia: '' }}
          onSubmit={handleSubmitForm}
        >
          {({ isSubmitting }) => (
            <Form>
              {/*<FieldWithErrorMessage
                type="text"
                name="nombreFantasia"
                label="Nombre Fantasía"
                placeholder="¿Cómo te gustaría que te llamemos?"
              />*/}
              <div className='shadow-md my-3 rounded-b-md'>
                <div className='mb-2 flex justify-center bg-primary h-[74px] py-3'>
                  <img src={tituloLoginSII} alt='tituloLoginSII' />
                </div>

                <ul className='flex flex-col gap-3 px-6 pb-6'>
                  <li>
                    <FieldWithErrorMessage
                      type='text'
                      name='user'
                      label='RUT'
                      placeholder='Ej: 123456789'
                    />
                  </li>
                  <li>
                    <FieldWithErrorMessage
                      type='password'
                      name='password'
                      label='Clave Tributaria'
                      placeholder='Ingresa tu clave tributaria'
                    />
                  </li>
                  <li>
                    <button
                      disabled={isSubmitting}
                      type='submit'
                      className='btn-primary w-full text-sm'
                    >
                      AGREGAR EMPRESA
                    </button>
                  </li>
                </ul>
              </div>
            </Form>
          )}
          {/*<div className="flex flex-col items-center">
          <input id="upload-photo" type="file" className="hidden" onChange={handleImageUpload} />
          <figure className="cursor-pointer relative" onClick={() => document.getElementById('upload-photo').click()}>
            <img className="rounded-full w-24 h-24 object-cover" src={imageUrl} alt="placeholder" />
            <img src={iconLapiz} alt="iconLapiz" className="absolute bottom-0 right-0 z-10 bg-white rounded-full w-8 h-8 shadow-md" />
          </figure>
        </div>*/}
        </Formik>
      </div>
    </section>
  )
}
